import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { reportsAPI } from '../services/api';
import CustomTable from '../components/CustomTable';
import Toast from '../components/Toast';
import { 
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import './ReportsPage.css';

const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#06b6d4'];

// Funciones helper fuera del componente
function getDefaultStartDate() {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
}

function getTodayDate() {
    return new Date().toISOString().split('T')[0];
}

const ReportsPage = () => {
    const isMobile = window.innerWidth < 768;
    const [reportData, setReportData] = useState(null);
    const [reportColumns, setReportColumns] = useState([]);
    const [kpiData, setKpiData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('table');
    const [toast, setToast] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [params, setParams] = useState({ 
        reportType: 'sales', 
        start_date: getDefaultStartDate(), 
        end_date: getTodayDate(),
        group_by: 'detail',
        limit: 20,
        sort_by: 'total_spent'
    });

    // useEffect para limpiar datos al cambiar tipo de reporte
    useEffect(() => {
        setReportData(null);
        setKpiData(null);
        setActiveTab('table');
    }, [params.reportType]);

    const handleParamChange = (e) => {
        const { name, value } = e.target;
        setParams(prev => ({ ...prev, [name]: value }));
    };

    const showToast = (message, type = 'error') => setToast({ message, type });

    const validateDates = () => {
        if (params.reportType !== 'inventory' && (!params.start_date || !params.end_date)) {
            showToast('Por favor, selecciona un rango de fechas.', 'warning');
            return false;
        }
        
        if (params.start_date && params.end_date) {
            const start = new Date(params.start_date);
            const end = new Date(params.end_date);
            if (start > end) {
                showToast('La fecha de inicio debe ser anterior a la fecha de fin.', 'warning');
                return false;
            }
        }
        
        return true;
    };

    const calculateKPIs = (data, reportType) => {
        if (!data || data.length === 0) return null;

        switch (reportType) {
            case 'sales':
                if (params.group_by === 'detail') {
                    const totalRevenue = data.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
                    const totalProfit = data.reduce((sum, item) => sum + parseFloat(item.profit || 0), 0);
                    const salesCount = data.length;
                    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                    const avgTicket = salesCount > 0 ? totalRevenue / salesCount : 0;

                    return {
                        total_revenue: totalRevenue,
                        total_profit: totalProfit,
                        sales_count: salesCount,
                        profit_margin: profitMargin,
                        avg_ticket: avgTicket
                    };
                } else {
                    const totalRevenue = data.reduce((sum, item) => sum + parseFloat(item.total_sales || 0), 0);
                    const totalProfit = data.reduce((sum, item) => sum + parseFloat(item.total_profit || 0), 0);
                    const salesCount = data.reduce((sum, item) => sum + parseInt(item.sales_count || 0), 0);
                    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

                    return {
                        total_revenue: totalRevenue,
                        total_profit: totalProfit,
                        sales_count: salesCount,
                        profit_margin: profitMargin
                    };
                }

            case 'top_products':
                const totalRevenue = data.reduce((sum, item) => sum + parseFloat(item.total_revenue || 0), 0);
                const totalProfit = data.reduce((sum, item) => sum + parseFloat(item.total_profit || 0), 0);
                const totalUnits = data.reduce((sum, item) => sum + parseInt(item.quantity_sold || 0), 0);
                const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

                return {
                    total_revenue: totalRevenue,
                    total_profit: totalProfit,
                    total_sales: totalUnits,
                    profit_margin: profitMargin
                };

            case 'customers':
                const totalSpent = data.reduce((sum, item) => sum + parseFloat(item.total_spent || 0), 0);
                const totalPurchases = data.reduce((sum, item) => sum + parseInt(item.purchase_count || 0), 0);
                const avgPurchase = data.length > 0 ? totalSpent / data.length : 0; // REPARADO: Evitar división por cero

                return {
                    total_revenue: totalSpent,
                    total_sales: totalPurchases,
                    avg_ticket: avgPurchase,
                    sales_count: data.length
                };

            case 'profitability':
                const revenue = data.reduce((sum, item) => sum + parseFloat(item.revenue || 0), 0);
                const profit = data.reduce((sum, item) => sum + parseFloat(item.profit || 0), 0);
                const margin = data.length > 0 ? data.reduce((sum, item) => sum + parseFloat(item.margin_percent || 0), 0) / data.length : 0;

                return {
                    total_revenue: revenue,
                    total_profit: profit,
                    profit_margin: margin,
                    sales_count: data.length
                };

            case 'inventory':
                const totalValue = data.reduce((sum, item) => sum + parseFloat(item.stock_value || 0), 0);
                const lowStock = data.filter(item => item.stock_status === 'Stock bajo').length;
                const outOfStock = data.filter(item => item.stock_status === 'Sin stock').length;

                return {
                    total_revenue: totalValue,
                    total_sales:  data.length,
                    sales_count:  lowStock,
                    avg_ticket:   outOfStock,
                    // Labels específicos para inventario — usados como overrides en renderKPIs
                    labels: {
                        card1: 'Valor Inventario',
                        card2: 'Total Productos',
                        card3: 'Stock Bajo',
                        card4: 'Sin Stock'
                    }
                };

            case 'financial':
                return data[0] || {};

            case 'expenses':
                const totalExpenses = data.reduce((sum, item) => sum + parseFloat(item.amount || item.total_amount || 0), 0);
                return {
                    total_revenue: totalExpenses,
                    total_sales: data.length,
                    labels: {
                        card1: 'Total Gastos',
                        card2: 'N° Registros',
                        card3: '',
                        card4: ''
                    }
                };

            default:
                return null;
        }
    };

    const generateReport = async () => {
        if (!validateDates()) return;

        setIsLoading(true);
        setReportData(null);
        setKpiData(null);
        setCurrentPage(1);
        
        try {
            let response;
            let data;
            let columns = [];

            switch (params.reportType) {
                case 'sales':
                    response = await reportsAPI.getSalesReport({ 
                        start_date: params.start_date, 
                        end_date: params.end_date,
                        group_by: params.group_by
                    });
                    data = response.data.data.salesReport;
                    
                    if (params.group_by === 'detail') {
                        columns = [
                            { header: 'Fecha', accessor: 'created_at', render: (date) => date || '-' },
                            { header: 'N° Venta', accessor: 'sale_id', render: (val) => val || '-' },
                            { header: 'Cliente', accessor: 'customer_name', render: (val) => val || 'Sin cliente' },
                            { header: 'N° Pedido', accessor: 'order_reference', render: (val) => val || '-' },
                            { header: 'Total', accessor: 'total_amount', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                            { header: 'Ganancia', accessor: 'profit', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        ];
                    } else {
                        columns = [
                            { header: 'Período', accessor: 'period' },
                            { header: 'N° Ventas', accessor: 'sales_count' },
                            { header: 'Total Ventas', accessor: 'total_sales', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                            { header: 'Ganancia', accessor: 'total_profit', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                            { header: 'Ticket Promedio', accessor: 'average_sale', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        ];
                    }
                    break;
                
                case 'inventory':
                    response = await reportsAPI.getInventoryReport();
                    data = response.data.data.products;
                    columns = [
                        { header: 'Código', accessor: 'code' },
                        { header: 'Nombre', accessor: 'name' },
                        { header: 'Categoría', accessor: 'category', render: (val) => val || '-' },
                        { header: 'Stock', accessor: 'stock', render: (val, row) => row.unit === 'kg' || row.unit === 'litro' ? parseFloat(val).toFixed(2) : parseInt(val, 10) },
                        { header: 'Stock Mín', accessor: 'min_stock' },
                        { header: 'Estado', accessor: 'stock_status', render: (val) => {
                            const colors = { 'Sin stock': '🔴', 'Stock bajo': '🟡', 'Stock normal': '🟢' };
                            return `${colors[val] || ''} ${val}`;
                        }},
                        { header: 'Valor Inventario', accessor: 'stock_value', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                    ];
                    break;

                case 'top_products':
                    response = await reportsAPI.getTopProductsReport({
                        start_date: params.start_date,
                        end_date: params.end_date,
                        limit: params.limit
                    });
                    data = response.data.data.products;
                    columns = [
                        { header: '#', accessor: (row, index) => index + 1 },
                        { header: 'Código', accessor: 'code' },
                        { header: 'Producto', accessor: 'name' },
                        { header: 'Categoría', accessor: 'category', render: (val) => val || '-' },
                        { header: 'Cantidad Vendida', accessor: 'quantity_sold', render: (val) => { const n = parseFloat(val); return n % 1 === 0 ? n.toFixed(0) : n.toFixed(2); } },
                        { header: 'Ingresos', accessor: 'total_revenue', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Ganancia', accessor: 'total_profit', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                    ];
                    break;

                case 'customers':
                    response = await reportsAPI.getCustomerReport({
                        start_date: params.start_date,
                        end_date: params.end_date,
                        limit: params.limit,
                        sort_by: params.sort_by
                    });
                    data = response.data.data.customers;
                    columns = [
                        { header: 'Cliente', accessor: 'name' },
                        { header: 'Email', accessor: 'email', render: (val) => val || '-' },
                        { header: 'Teléfono', accessor: 'phone', render: (val) => val || '-' },
                        { header: 'N° Compras', accessor: 'purchase_count' },
                        { header: 'Total Gastado', accessor: 'total_spent', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Ticket Promedio', accessor: 'avg_purchase', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Última Compra', accessor: 'last_purchase', render: (val) => val ? new Date(val).toLocaleDateString('es-PE') : '-' },
                    ];
                    break;

                case 'profitability':
                    response = await reportsAPI.getProfitabilityReport({
                        start_date: params.start_date,
                        end_date: params.end_date,
                        group_by: params.group_by
                    });
                    data = response.data.data.report;
                    columns = [
                        { header: params.group_by === 'product' ? 'Producto' : params.group_by === 'category' ? 'Categoría' : 'Período', accessor: (params.group_by === 'product' || params.group_by === 'category') ? 'name' : 'period' },
                        { header: 'Unidades', accessor: 'units_sold' },
                        { header: 'Ingresos', accessor: 'revenue', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Costo', accessor: 'cost', render: (val) => `S/. ${parseFloat(val || 0).toFixed(2)}` },
                        { header: 'Ganancia', accessor: 'profit', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Margen %', accessor: 'margin_percent', render: (val) => `${parseFloat(val || 0).toFixed(1)}%` },
                    ];
                    break;

                case 'financial':
                    response = await reportsAPI.getFinancialReport({
                        start_date: params.start_date,
                        end_date: params.end_date
                    });
                    data = [response.data.data.summary];
                    columns = [
                        { header: 'Total Ventas', accessor: 'total_sales' },
                        { header: 'Ingresos Totales', accessor: 'total_revenue', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Ganancia Total', accessor: 'total_profit', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Ticket Promedio', accessor: 'avg_ticket', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Margen %', accessor: 'profit_margin', render: (val) => `${parseFloat(val || 0).toFixed(1)}%` },
                    ];
                    break;

                case 'expenses':
                    response = await reportsAPI.getExpenseReport({ 
                        start_date: params.start_date, 
                        end_date: params.end_date,
                        group_by: params.group_by
                    });
                    data = response.data.data;
                    columns = [
                        { header: 'Descripción', accessor: 'description', render: (val) => val || '-' },
                        { header: 'Categoría', accessor: 'category', render: (val) => val || 'Sin categoría' },
                        { header: 'Monto', accessor: 'amount', render: (val) => `S/. ${parseFloat(val).toFixed(2)}` },
                        { header: 'Fecha', accessor: 'expense_date', render: (val) => val ? val.split('T')[0] : '-' },
                    ];
                    break;

                default:
                    throw new Error('Tipo de reporte no válido');
            }
            
            setReportData(data);
            setReportColumns(columns);
            setKpiData(calculateKPIs(data, params.reportType));

        } catch (error) {
            console.error("Error generando el reporte:", error);
            showToast(error.response?.data?.message || 'Error al generar el reporte', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const exportCSV = async () => {
        if (!validateDates()) return;

        try {
            const token = localStorage.getItem('token');
            // Usa la misma variable de entorno que services/api.js
            // REACT_APP_API_URL termina en '/api', separamos el origin para construir
            // la URL completa con el path /api/reports/...
            const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const baseURL = apiBase.endsWith('/api') 
                ? apiBase.slice(0, -4)   // quita el '/api' final → queda el origin
                : apiBase;
            
            const queryParams = new URLSearchParams({ format: 'csv' });

            if (params.start_date) queryParams.append('start_date', params.start_date);
            if (params.end_date) queryParams.append('end_date', params.end_date);

            let endpoint = '';
            switch (params.reportType) {
                case 'sales':
                    queryParams.append('group_by', params.group_by);
                    endpoint = '/api/reports/sales';
                    break;
                case 'inventory':
                    endpoint = '/api/reports/inventory';
                    break;
                case 'top_products':
                    queryParams.append('limit', params.limit);
                    endpoint = '/api/reports/top-products';
                    break;
                case 'customers':
                    queryParams.append('limit', params.limit);
                    queryParams.append('sort_by', params.sort_by);
                    endpoint = '/api/reports/customers';
                    break;
                case 'profitability':
                    queryParams.append('group_by', params.group_by);
                    endpoint = '/api/reports/profitability';
                    break;
                case 'financial':
                    endpoint = '/api/reports/financial';
                    break;
                default:
                    showToast('Exportación no disponible para este tipo de reporte', 'warning');
                    return;
            }

            const url = `${baseURL}${endpoint}?${queryParams.toString()}`;
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error('Error al descargar el archivo');
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `reporte-${params.reportType}-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error exportando CSV:', error);
            showToast('Error al exportar el reporte', 'error');
        }
    };

    const reportTypeNames = {
        sales: 'Ventas por Período',
        top_products: 'Productos Más Vendidos', 
        customers: 'Análisis de Clientes',
        profitability: 'Rentabilidad',
        inventory: 'Inventario General',
        financial: 'Reporte Financiero',
        expenses: 'Gastos por Período'
    };

    const exportExcel = async () => {
        if (!reportData || reportData.length === 0) {
            showToast('No hay datos para exportar', 'warning');
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Reporte');
        const columns = reportColumns.map(col => col.header);
        
        // Título
        sheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);
        const titleCell = sheet.getCell('A1');
        titleCell.value = `REPORTE: ${reportTypeNames[params.reportType] || params.reportType}`;
        titleCell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        sheet.addRow([]);
        
        // Encabezados
        const headerRow = sheet.addRow(columns);
        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2980B9' } };
        });
        
        // Datos con filas alternas
        reportData.forEach((row, index) => {
            const values = reportColumns.map(col => 
                typeof col.accessor === 'function' ? col.accessor(row, index) : row[col.accessor]
            );
            const dataRow = sheet.addRow(values);
            const bgColor = index % 2 === 0 ? 'FFFFFFFF' : 'FFF8F8F8';
            dataRow.eachCell(cell => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
            });
        });
        
        // Anchos automáticos
        sheet.columns.forEach(col => { col.width = 18; });
        sheet.getColumn(2).alignment = { horizontal: 'center' };
        
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte-${reportTypeNames[params.reportType] || params.reportType}-${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const renderKPIs = () => {
        if (!kpiData) return null;

        const formatCurrency = (value) => `S/. ${parseFloat(value || 0).toFixed(2)}`;
        // Usar labels personalizados del reporte si existen, sino los genéricos
        const L = kpiData.labels || {};

        // Card 4: para inventario los valores son conteos, no moneda
        const isInventory = params.reportType === 'inventory';

        return (
            <div className="kpis-container">
                <div className="kpi-card">
                    <div className="kpi-icon sales">💰</div>
                    <div className="kpi-content">
                        <div className="kpi-label">{L.card1 || 'Total Ventas'}</div>
                        <div className="kpi-value">{formatCurrency(kpiData.total_revenue || kpiData.total_sales)}</div>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon profit">📈</div>
                    <div className="kpi-content">
                        <div className="kpi-label">{L.card2 || 'Ganancia Total'}</div>
                        <div className="kpi-value">
                            {isInventory
                                ? (kpiData.total_sales || 0)
                                : formatCurrency(kpiData.total_profit)}
                        </div>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon margin">💹</div>
                    <div className="kpi-content">
                        <div className="kpi-label">{L.card3 || 'Margen Beneficio'}</div>
                        <div className="kpi-value">
                            {isInventory
                                ? (kpiData.sales_count || 0)
                                : (kpiData.profit_margin ? `${parseFloat(kpiData.profit_margin).toFixed(1)}%` : 'N/A')}
                        </div>
                    </div>
                </div>

                <div className="kpi-card">
                    <div className="kpi-icon count">🛒</div>
                    <div className="kpi-content">
                        <div className="kpi-label">{L.card4 || 'N° de Ventas'}</div>
                        <div className="kpi-value">
                            {isInventory
                                ? (kpiData.avg_ticket || 0)
                                : (kpiData.total_sales || kpiData.sales_count || 0)}
                        </div>
                        {!isInventory && kpiData.avg_ticket && (
                            <div className="kpi-subtext">Ticket prom: {formatCurrency(kpiData.avg_ticket)}</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // AQUÍ ESTÁN TODOS TUS GRÁFICOS ORIGINALES, PROTEGIDOS CONTRA EL ERROR DE LENGTH
    const renderCharts = () => {
        // ✅ REPARACIÓN: Validación de seguridad para que no explote si no hay datos o se están cargando
        if (!reportData || !Array.isArray(reportData) || reportData.length === 0 || isLoading) return null;

        const formatCurrency = (value) => `S/. ${parseFloat(value).toFixed(0)}`;

        switch (params.reportType) {
            case 'sales':
                const trendData = reportData.slice(0, 30).map(item => ({
                    period: item.period || item.created_at || 'N/A',
                    ventas: parseFloat(item.total_sales || item.total_amount || 0),
                    ganancia: parseFloat(item.total_profit || item.profit || 0)
                }));

                return (
                    <div className="charts-grid">
                        <div className="chart-card full-width">
                            <h3>📈 Tendencia de Ventas y Ganancias</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <LineChart data={trendData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="period" stroke="#6b7280" style={{ fontSize: '12px' }} interval="preserveStartEnd" />
                                    <YAxis stroke="#6b7280" style={{ fontSize: '12px' }} tickFormatter={formatCurrency} />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Line type="monotone" dataKey="ventas" stroke="#6366f1" strokeWidth={2} />
                                    <Line type="monotone" dataKey="ganancia" stroke="#10b981" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'top_products':
                const barData = reportData.slice(0, 10).map(item => ({
                    name: (item.name || 'N/A').length > 20 ? (item.name || 'N/A').substring(0, 17) + '...' : (item.name || 'N/A'),
                    cantidad: parseInt(item.quantity_sold || 0),
                    ingresos: parseFloat(item.total_revenue || 0)
                }));

                const pieData = reportData.slice(0, 10).map(item => ({
                    name: (item.name || 'N/A').length > 15 ? (item.name || 'N/A').substring(0, 12) + '...' : (item.name || 'N/A'),
                    value: parseFloat(item.total_revenue || 0)
                }));

                return (
                    <div className="charts-grid">
                        <div className="chart-card">
                            <h3>🏆 Top 10 Productos por Cantidad</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <BarChart data={barData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis type="number" stroke="#6b7280" interval="preserveStartEnd" />
                                    <YAxis dataKey="name" type="category" width={100} stroke="#6b7280" style={{ fontSize: '11px' }} />
                                    <Tooltip />
                                    <Bar dataKey="cantidad" fill="#6366f1" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card">
                            <h3>💰 Distribución de Ingresos</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'customers':
                const customerData = reportData.slice(0, 10).map(item => ({
                    name: (item.name || 'N/A').length > 20 ? (item.name || 'N/A').substring(0, 17) + '...' : (item.name || 'N/A'),
                    compras: parseInt(item.purchase_count || 0),
                    gastado: parseFloat(item.total_spent || 0)
                }));

                return (
                    <div className="charts-grid">
                        <div className="chart-card">
                            <h3>👥 Top 10 Clientes por Compras</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <BarChart data={customerData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="name" stroke="#6b7280" angle={-45} textAnchor="end" height={100} style={{ fontSize: '11px' }} interval="preserveStartEnd" />
                                    <YAxis stroke="#6b7280" />
                                    <Tooltip />
                                    <Bar dataKey="compras" fill="#8b5cf6" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card">
                            <h3>💵 Top 10 Clientes por Gasto</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <BarChart data={customerData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="name" stroke="#6b7280" angle={-45} textAnchor="end" height={100} style={{ fontSize: '11px' }} interval="preserveStartEnd" />
                                    <YAxis stroke="#6b7280" tickFormatter={formatCurrency} />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Bar dataKey="gastado" fill="#10b981" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'profitability':
                const profData = reportData.slice(0, 10).map(item => ({
                    name: (item.name || item.period || '').length > 15 ? (item.name || item.period || '').substring(0, 12) + '...' : (item.name || item.period || ''),
                    ingresos: parseFloat(item.revenue || 0),
                    ganancia: parseFloat(item.profit || 0)
                }));

                return (
                    <div className="charts-grid">
                        <div className="chart-card full-width">
                            <h3>💹 Análisis de Rentabilidad</h3>
                            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                                <BarChart data={profData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis dataKey="name" stroke="#6b7280" angle={-45} textAnchor="end" height={100} style={{ fontSize: '11px' }} interval="preserveStartEnd" />
                                    <YAxis stroke="#6b7280" tickFormatter={formatCurrency} />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Bar dataKey="ingresos" fill="#6366f1" />
                                    <Bar dataKey="ganancia" fill="#10b981" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            case 'inventory':
                const stockStatus = reportData.reduce((acc, item) => {
                    const status = item.stock_status || 'Stock normal';
                    acc[status] = (acc[status] || 0) + 1;
                    return acc;
                }, {});

                const invPieData = Object.entries(stockStatus).map(([name, value]) => ({ name, value }));

                return (
                    <div className="charts-grid">
                        <div className="chart-card">
                            <h3>📦 Estado del Inventario</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={invPieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {invPieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const renderFilters = () => {
        return (
            <>
                {params.reportType !== 'inventory' && (
                    <>
                        <div className="form-group">
                            <label>Fecha Desde</label>
                            <input 
                                type="date" 
                                name="start_date" 
                                value={params.start_date} 
                                onChange={handleParamChange}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                        <div className="form-group">
                            <label>Fecha Hasta</label>
                            <input 
                                type="date" 
                                name="end_date" 
                                value={params.end_date} 
                                onChange={handleParamChange}
                                max={new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </>
                )}

                {params.reportType === 'sales' && (
                    <div className="form-group">
                        <label>Agrupar por</label>
                        <select name="group_by" value={params.group_by} onChange={handleParamChange}>
                            <option value="detail">Detallado</option>
                            <option value="day">Por Día</option>
                            <option value="month">Por Mes</option>
                        </select>
                    </div>
                )}
            </>
        );
    };
    
    const totalPages = reportData ? Math.ceil(reportData.length / itemsPerPage) : 0;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentData = reportData ? reportData.slice(startIndex, endIndex) : [];

    const goToPage = (page) => setCurrentPage(page);
    const goToPreviousPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
    const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1); };

    return (
        <div className="content-pane">
            <div className="page-header">
                <h2>📊 Reportes y Análisis</h2>
                <p className="page-subtitle">Visualiza y analiza el rendimiento de tu negocio</p>
            </div>
            
            <div className="reports-filters">
                <div className="form-grid">
                    <div className="form-group">
                        <label>Tipo de Reporte</label>
                        <select name="reportType" value={params.reportType} onChange={handleParamChange}>
                            <option value="sales">📈 Ventas por Período</option>
                            <option value="top_products">🏆 Productos Más Vendidos</option>
                            <option value="customers">👥 Análisis de Clientes</option>
                            <option value="profitability">💰 Rentabilidad</option>
                            <option value="inventory">📦 Inventario General</option>
                            <option value="financial">💵 Reporte Financiero</option>
                            <option value="expenses">💸 Gastos por Período</option>
                        </select>
                    </div>
                    
                    {renderFilters()}
                    
                    <div className="form-group">
                        <button 
                            onClick={generateReport} 
                            className="btn btn-primary" 
                            disabled={isLoading}
                        >
                            {isLoading ? '⏳...' : '📈 Generar Reporte'}
                        </button>
                    </div>
                </div>
            </div>

            {kpiData && !isLoading && renderKPIs()}

            {reportData && reportData.length > 0 && !isLoading && (
                <>
                    <div className="report-tabs">
                        <button 
                            className={`tab-btn ${activeTab === 'charts' ? 'active' : ''}`}
                            onClick={() => setActiveTab('charts')}
                        >
                            📊 Gráficos
                        </button>
                        <button 
                            className={`tab-btn ${activeTab === 'table' ? 'active' : ''}`}
                            onClick={() => setActiveTab('table')}
                        >
                            📋 Tabla
                        </button>
                        <button onClick={() => exportExcel().catch(err => console.error(err))} className="btn-excel" style={{ background: '#1e7e34', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                            📗 Excel
                        </button>
                    </div>

                    <div className="report-content">
                        {activeTab === 'charts' ? renderCharts() : (
                            <>
                                <div className="pagination-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <p style={{ margin: 0 }}>📋 Mostrando {reportData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, reportData.length)} de {reportData.length} registros</p>
                                    <div className="items-per-page" style={{ display: 'flex', alignItems: 'center' }}>
                                        <label style={{ marginRight: '8px' }}>Mostrar:</label>
                                        <select 
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <input 
                                            type="number"
                                            placeholder="..."
                                            min="1"
                                            max="500"
                                            style={{ width: '60px', marginLeft: '5px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            onBlur={(e) => {
                                                const val = Number(e.target.value);
                                                if (val >= 1 && val <= 500) {
                                                    setItemsPerPage(val);
                                                    setCurrentPage(1);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    const val = Number(e.target.value);
                                                    if (val >= 1 && val <= 500) {
                                                        setItemsPerPage(val);
                                                        setCurrentPage(1);
                                                    }
                                                }
                                            }}
                                        />
                                        <span style={{ marginLeft: '5px' }}>por página</span>
                                    </div>
                                </div>
                                <CustomTable columns={reportColumns} data={currentData} />
                                {totalPages > 1 && (
                                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '15px' }}>
                                        <button 
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1}
                                            className="btn-page-nav"
                                        >
                                            ← Anterior
                                        </button>

                                        <div className="page-numbers" style={{ display: 'flex', gap: '5px' }}>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                                                if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                                                    return (
                                                        <button
                                                            key={page}
                                                            onClick={() => goToPage(page)}
                                                            className={`btn-page ${page === currentPage ? 'active' : ''}`}
                                                        >
                                                            {page}
                                                        </button>
                                                    );
                                                } else if (page === currentPage - 3 || page === currentPage + 3) {
                                                    return <span key={page} className="page-dots">...</span>;
                                                }
                                                return null;
                                            })}
                                        </div>

                                        <button 
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                            className="btn-page-nav"
                                        >
                                            Siguiente →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </>
            )}

            {isLoading && (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Generando reporte...</p>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </div>
    );
};

export default ReportsPage;